import { DateTime } from 'luxon';
import { APP_TIMEZONE } from './constants';

/**
 * Time helpers. Storage is UTC; everything user-facing is Europe/Warsaw.
 * Local dates are ISO strings `yyyy-MM-dd`, local times `HH:mm`.
 */

export type DateInput = Date | string | number;

export function toLocal(value: DateInput): DateTime {
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : typeof value === 'number'
        ? DateTime.fromMillis(value)
        : DateTime.fromISO(value, { setZone: true });
  return dt.setZone(APP_TIMEZONE).setLocale('pl');
}

/** Wall-clock time in Warsaw → UTC instant. Throws on malformed input. */
export function localToUtc(date: string, time: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) {
    throw new RangeError(`Invalid local date/time: ${date} ${time}`);
  }
  const dt = DateTime.fromISO(`${date}T${time}`, { zone: APP_TIMEZONE });
  if (!dt.isValid) throw new RangeError(`Invalid local date/time: ${date} ${time}`);
  return dt.toUTC().toJSDate();
}

/** UTC bounds [start, end) of a local calendar day (23 or 25 hours on DST change days). */
export function localDayRangeUtc(date: string): { start: Date; end: Date } {
  const start = DateTime.fromISO(date, { zone: APP_TIMEZONE }).startOf('day');
  if (!start.isValid) throw new RangeError(`Invalid local date: ${date}`);
  return { start: start.toUTC().toJSDate(), end: start.plus({ days: 1 }).toUTC().toJSDate() };
}

/** Today's local date in Warsaw, `yyyy-MM-dd`. */
export function localToday(now: DateInput = new Date()): string {
  return toLocal(now).toISODate()!;
}

export function localDateOf(value: DateInput): string {
  return toLocal(value).toISODate()!;
}

export function localTimeOf(value: DateInput): string {
  return toLocal(value).toFormat('HH:mm');
}

export function addDaysLocal(date: string, days: number): string {
  return DateTime.fromISO(date, { zone: APP_TIMEZONE }).plus({ days }).toISODate()!;
}

/** Calendar month arithmetic that clamps to the month end (31 Jan + 1 month → 28/29 Feb). */
export function addMonthsLocal(date: string, months: number): string {
  return DateTime.fromISO(date, { zone: APP_TIMEZONE }).plus({ months }).toISODate()!;
}

/** "wtorek, 21 października" */
export function formatDayLong(value: DateInput): string {
  return toLocal(value).toFormat('EEEE, d MMMM');
}

/** "21.10.2026" */
export function formatDateShort(value: DateInput): string {
  return toLocal(value).toFormat('dd.LL.yyyy');
}

/** "21.10.2026, 14:30" */
export function formatDateTime(value: DateInput): string {
  return toLocal(value).toFormat('dd.LL.yyyy, HH:mm');
}

/** "14:30" */
export function formatTime(value: DateInput): string {
  return toLocal(value).toFormat('HH:mm');
}

/** Number and unit never split across lines (Polish typography): a no-break space. */
const NBSP = '\u00a0';

/** Human duration: 90 → "1,5 h", 45 → "45 min", 60 → "1 h" (no-break space before the unit). */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}${NBSP}min`;
  const hours = minutes / 60;
  if (Number.isInteger(hours)) return `${hours}${NBSP}h`;
  if (minutes % 30 === 0) return `${String(hours).replace('.', ',')}${NBSP}h`;
  return `${Math.floor(hours)}${NBSP}h ${minutes % 60}${NBSP}min`;
}
